<svelte:options runes={false} />
<script>
  // Pace chart (TASK-20260823-twap-strategies-tca): % of schedule elapsed (x) vs % filled (y) per
  // child against the diagonal schedule with its ±2 % band; posture escalations (the ladder stepping
  // to a more aggressive posture) are marked and named. Ahead/behind is read from the point's side of
  // the band AND the readout, never color alone.
  import { linearScale, areaPath, linePath } from '../charts/svgScales.js';

  export let series = null;   // from paceSeries(children, parent)

  const H = 220;
  const PAD = { top: 12, right: 16, bottom: 28, left: 40 };
  let width = 320;
  let active = null;
  // A marker is a real button: Enter/Space (or click) pins its readout so a keyboard user can read it
  // after moving on; the same key on the pinned marker releases it.
  let pinned = null;
  function togglePin(p) { pinned = pinned === p ? null : p; active = pinned ?? p; }
  function onMarkerKey(e, p) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePin(p); }
  }

  $: points = series?.points ?? [];
  $: hasData = points.length > 0;
  $: x = linearScale([0, 100], [PAD.left, Math.max(200, width) - PAD.right], { clamp: true });
  $: y = linearScale([0, 100], [H - PAD.bottom, PAD.top], { clamp: true });
  $: band = series?.band ?? 2;
  $: bandPts = [0, 100].map((p) => ({ p, hi: Math.min(100, p + band), lo: Math.max(0, p - band) }));
  $: bandPath = areaPath(bandPts, (d) => x(d.p), (d) => y(d.hi), (d) => y(d.lo));
  $: fillPath = linePath(points, (p) => x(p.pctTime), (p) => y(p.pctFilled));
  $: escalations = series?.escalations ?? [];
  $: ticks = [0, 25, 50, 75, 100];

  function readout(p) {
    const filled = p.pctFilled === null ? 'fill % unknown' : `${p.pctFilled.toFixed(1)} % filled`;
    const elapsed = p.pctTime === null ? 'time % unknown' : `${p.pctTime.toFixed(1)} % of schedule`;
    const pace = p.pctFilled === null || p.pctTime === null ? '' : p.pctFilled - p.pctTime > band ? ' · ahead' : p.pctTime - p.pctFilled > band ? ' · behind' : ' · on pace';
    return `${elapsed} → ${filled}${pace} · ${p.postureName ?? 'posture ?'}`;
  }
</script>

<div class="pace" bind:clientWidth={width}>
  {#if !hasData}
    <p class="pace-empty">No slices placed yet.</p>
  {:else}
    <svg class="pace-svg" viewBox={`0 0 ${Math.max(200, width)} ${H}`} width="100%" height={H} role="img"
      aria-label={`Pace: ${points.length} slices against the schedule with a ±${band} % band; ${escalations.length} posture escalation${escalations.length === 1 ? '' : 's'}`}>
      {#each ticks as t}
        <line x1={PAD.left} x2={Math.max(200, width) - PAD.right} y1={y(t)} y2={y(t)} class="pace-grid" />
        <text x={PAD.left - 6} y={y(t) + 3} class="pace-tick" text-anchor="end">{t} %</text>
        <text x={x(t)} y={H - 8} class="pace-tick" text-anchor="middle">{t} %</text>
      {/each}
      <path d={bandPath} class="pace-band" />
      <line x1={x(0)} y1={y(0)} x2={x(100)} y2={y(100)} class="pace-schedule" />
      <path d={fillPath} class="pace-fill" />
      {#each points as p, i (p.childOrderId ?? i)}
        {#if p.pctTime !== null && p.pctFilled !== null}
          <circle cx={x(p.pctTime)} cy={y(p.pctFilled)} r={active === p ? 5.5 : 4} class="pace-pt" tabindex="0" role="button" aria-label={readout(p)}
            aria-pressed={pinned === p}
            on:mouseenter={() => (active = p)} on:mouseleave={() => (active = pinned)} on:focus={() => (active = p)} on:blur={() => (active = pinned)}
            on:click={() => togglePin(p)} on:keydown={(e) => onMarkerKey(e, p)}>
            <title>{readout(p)}</title>
          </circle>
        {/if}
      {/each}
      {#each escalations as e}
        {#if points[e.index]?.pctTime !== null}
          <g class="pace-esc" transform={`translate(${x(points[e.index].pctTime)}, ${PAD.top + 8})`}>
            <path d="M0,-6L5,3L-5,3Z" />
            <text x="7" y="3">→ {e.toName}</text>
          </g>
        {/if}
      {/each}
      <text x={Math.max(200, width) - PAD.right} y={H - PAD.bottom - 4} class="pace-axis" text-anchor="end">% of schedule elapsed → · ↑ % filled</text>
    </svg>
    <div class="pace-readout" aria-live="polite">{active ? readout(active) : `Band ±${band} % of the schedule; above the band = ahead, below = behind.`}</div>
  {/if}
</div>

<style>
  .pace { width: 100%; min-width: 0; }
  .pace-svg { display: block; border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); background: var(--bg-secondary, #111214); }
  .pace-grid { stroke: var(--chart-grid, #1e1f22); }
  .pace-tick, .pace-axis { fill: var(--text-secondary, #8a8b8d); font-size: 10px; font-variant-numeric: tabular-nums; }
  .pace-axis { font-size: 9px; fill: var(--text-muted, #6a6b6d); }
  .pace-band { fill: var(--color-primary, #00d4ff); opacity: 0.12; }
  .pace-schedule { stroke: var(--text-muted, #6a6b6d); stroke-dasharray: 4 3; }
  .pace-fill { fill: none; stroke: var(--color-primary, #00d4ff); stroke-width: 1.6; }
  .pace-pt { fill: var(--bg-secondary, #111214); stroke: var(--color-primary, #00d4ff); stroke-width: 1.8; cursor: pointer; }
  .pace-pt:focus-visible { outline: none; stroke-width: 3; stroke: var(--text-primary, #fff); }
  .pace-esc path { fill: var(--color-warning, #ffaa00); }
  .pace-esc text { fill: var(--color-warning, #ffaa00); font-size: 9px; }
  .pace-readout { min-height: 1.1rem; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); padding-top: 0.25rem; font-variant-numeric: tabular-nums; }
  .pace-empty { color: var(--text-muted, #6a6b6d); font-size: var(--font-size-sm, 0.85rem); }
</style>
